/**
 * Route -> backing-service map (Platform advisories v1 route scoping).
 *
 * Mirrors entitlement-codes.ts's ROUTE_ENTITLEMENT shape and prefix-walk resolution exactly (same
 * "keyed by the route's leading path segment(s)" convention) — but answers a DIFFERENT question.
 * ROUTE_ENTITLEMENT asks "what engine action gates this route"; ROUTE_SERVICES asks "which
 * backend service(s) does this route's data depend on". A route can appear in one map, the other,
 * both, or neither.
 *
 * Consumed by TbAdvisoryBannerComponent's `[services]` input (see app.component.ts): CRITICAL
 * advisories show on every route regardless of this map; WARN/INFO advisories show only on a
 * route whose backing services intersect the advisory's `affectedServices`.
 *
 * GROWS OVER TIME (by design, same convention as ROUTE_ENTITLEMENT): only the obvious, high-value
 * mappings are seeded here. A route with NO entry simply never shows a route-scoped (WARN/INFO)
 * advisory — it is not a bug, it is the documented default for an as-yet-unmapped route. Add an
 * entry whenever a route's advisory-relevant backing service becomes clear.
 *
 * Service ids are drawn from `@toybank/ui`'s ALL_BACKEND_SERVICES (mirrors `services/` 1:1) so a
 * typo here cannot silently desync from what an advisory's `affectedServices` entry can say.
 */
import type { BackendServiceId } from '@toybank/ui';

/**
 * Convenience uppercase-key lookup so the route table below reads like ENGINE_ACTION does.
 * Written out as literal properties (not derived via a Record<string,...> index signature) so
 * each access below is a known property, not an index-signature read — route-services.spec.ts
 * cross-checks every value against @toybank/ui's ALL_BACKEND_SERVICES so this can never silently
 * drift from the canonical id list.
 */
export const BACKEND_SERVICE = {
  ACCOUNTS: 'accounts',
  AGENT_GATEWAY: 'agent-gateway',
  BILLING: 'billing',
  CARDS: 'cards',
  CHANNEL: 'channel',
  COSESSION: 'cosession',
  DEPOSITS: 'deposits',
  ENTITLEMENTS: 'entitlements',
  ERP_CONNECTOR: 'erp-connector',
  FEES_INTEREST: 'fees-interest',
  FRAUD: 'fraud',
  IDENTITY: 'identity',
  IMPLEMENTATIONS: 'implementations',
  LEDGER: 'ledger',
  LENDING: 'lending',
  MERCHANT: 'merchant',
  NOTIFICATIONS: 'notifications',
  ONBOARDING: 'onboarding',
  ORIGINATION: 'origination',
  PARTY: 'party',
  PAYEE_VALIDATION: 'payee-validation',
  PAYMENTS: 'payments',
  RECEIVABLES: 'receivables',
  RECONCILIATION: 'reconciliation',
  RESTRUCTURE: 'restructure',
  SCREENING: 'screening',
  STATEMENTS: 'statements',
  TRADE: 'trade',
  TREASURY: 'treasury',
  TREASURY_POLICY: 'treasury-policy',
} as const satisfies Readonly<Record<string, BackendServiceId>>;

export const ROUTE_SERVICES: Readonly<Record<string, readonly BackendServiceId[]>> = {
  // Accounts & cash
  accounts: [BACKEND_SERVICE.ACCOUNTS, BACKEND_SERVICE.STATEMENTS],
  cash: [BACKEND_SERVICE.ACCOUNTS, BACKEND_SERVICE.STATEMENTS],
  reporting: [BACKEND_SERVICE.STATEMENTS],
  'consolidated-reporting': [BACKEND_SERVICE.ACCOUNTS, BACKEND_SERVICE.STATEMENTS],

  // Payments / transfers
  pay: [BACKEND_SERVICE.PAYMENTS],
  'payments/orders': [BACKEND_SERVICE.PAYMENTS],
  'payments/ach-collections': [BACKEND_SERVICE.PAYMENTS],
  'payments/disbursement': [BACKEND_SERVICE.PAYMENTS],
  'payments/managed-destinations': [BACKEND_SERVICE.PAYMENTS, BACKEND_SERVICE.PAYEE_VALIDATION],
  'payments/recalls': [BACKEND_SERVICE.PAYMENTS],
  'payments/order-approval': [BACKEND_SERVICE.PAYMENTS],
  'payments/rfp': [BACKEND_SERVICE.PAYMENTS],
  'payments/gpi-tracker': [BACKEND_SERVICE.PAYMENTS],
  'payments/app-claims': [BACKEND_SERVICE.PAYMENTS],
  bills: [BACKEND_SERVICE.PAYMENTS],
  batch: [BACKEND_SERVICE.PAYMENTS],
  scheduled: [BACKEND_SERVICE.PAYMENTS],
  templates: [BACKEND_SERVICE.PAYMENTS],
  beneficiaries: [BACKEND_SERVICE.PAYEE_VALIDATION, BACKEND_SERVICE.PAYMENTS],
  approvals: [BACKEND_SERVICE.PAYMENTS],
  'positive-pay': [BACKEND_SERVICE.FRAUD, BACKEND_SERVICE.PAYMENTS],
  'ach-debit-block': [BACKEND_SERVICE.FRAUD, BACKEND_SERVICE.PAYMENTS],
  disbursement: [BACKEND_SERVICE.PAYMENTS],
  'iso20022-import': [BACKEND_SERVICE.PAYMENTS],
  'anomaly-alerts': [BACKEND_SERVICE.FRAUD],

  // Security
  security: [BACKEND_SERVICE.IDENTITY],
  recovery: [BACKEND_SERVICE.IDENTITY],

  // Notifications
  notifications: [BACKEND_SERVICE.NOTIFICATIONS],
  'settings/notifications': [BACKEND_SERVICE.NOTIFICATIONS],

  // Entitlements & authority
  entitlements: [BACKEND_SERVICE.ENTITLEMENTS],
  'admin/entitlements': [BACKEND_SERVICE.ENTITLEMENTS],
  'agent-authority': [BACKEND_SERVICE.ENTITLEMENTS],
  authority: [BACKEND_SERVICE.ENTITLEMENTS],
  'admin/authenticator-fleet': [BACKEND_SERVICE.IDENTITY],
  'admin/security-center': [BACKEND_SERVICE.IDENTITY],

  // Cards
  cards: [BACKEND_SERVICE.CARDS],
  'admin/cards/mcc-mappings': [BACKEND_SERVICE.CARDS],

  // Treasury / trade / FX / liquidity
  trade: [BACKEND_SERVICE.TRADE],
  fx: [BACKEND_SERVICE.TREASURY],
  liquidity: [BACKEND_SERVICE.TREASURY],
  'treasury-services': [BACKEND_SERVICE.TREASURY],
  'customer-policy': [BACKEND_SERVICE.TREASURY_POLICY],

  // Lending / deposits
  lending: [BACKEND_SERVICE.LENDING],
  deposits: [BACKEND_SERVICE.DEPOSITS],

  // Receivables
  receivables: [BACKEND_SERVICE.RECEIVABLES],

  // Merchant
  merchant: [BACKEND_SERVICE.MERCHANT],

  // Reports / financial messages / file transfers
  reports: [BACKEND_SERVICE.STATEMENTS],
  'financial-messages': [BACKEND_SERVICE.CHANNEL],
  'file-transfers': [BACKEND_SERVICE.CHANNEL],

  // Integrations / ERP
  integrations: [BACKEND_SERVICE.ERP_CONNECTOR],

  // Onboarding / implementations / new business
  onboarding: [BACKEND_SERVICE.ONBOARDING],
  implementations: [BACKEND_SERVICE.IMPLEMENTATIONS],

  // Support / co-session
  'help/live-assist': [BACKEND_SERVICE.COSESSION],
  'support/cases': [BACKEND_SERVICE.COSESSION],

  // Ops / billing
  'admin/billing': [BACKEND_SERVICE.BILLING],
  'admin/reconciliation': [BACKEND_SERVICE.RECONCILIATION],
};

/**
 * Resolve the backing-service ids for a route path, walking from the most specific (full path) to
 * the leading segment — same prefix-walk algorithm as entitlement-codes.ts's routeEntitlement().
 * Returns an empty array for an unmapped route (the documented default: no route-scoped WARN/INFO
 * advisory ever shows there; CRITICAL is unaffected — see TbAdvisoryBannerComponent).
 */
export function routeServices(path: string): readonly BackendServiceId[] {
  const clean = (path ?? '').replace(/^\/+/, '').split('?')[0].split('#')[0];
  if (!clean) return [];
  const segments = clean.split('/');
  for (let i = segments.length; i >= 1; i--) {
    const prefix = segments.slice(0, i).join('/');
    if (Object.prototype.hasOwnProperty.call(ROUTE_SERVICES, prefix)) {
      return ROUTE_SERVICES[prefix];
    }
  }
  return [];
}
